import { useMemo, useState } from 'react';
import useDebounce from '../utils/useDebounce';
import { getPageOfSharedDrivesForDomain, UserKeys } from '../api/users-api';
import { Page, SharedDriveModel } from '../../types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';

interface SharedDrivesAutocompleteProps {
  autocompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  onDriveSelect?: (drive: SharedDriveModel | null) => void;
  size: 'small' | 'medium';
  disabled?: boolean;
  errors?: boolean;
  newKey?: string;
  helperText?: string;
  required?: boolean;
}

export default function SharedDrivesAutocomplete({
  autocompleteValue,
  setAutoCompleteValue,
  onDriveSelect,
  size,
  disabled,
  errors,
  helperText,
  newKey,
  required,
}: SharedDrivesAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, 500);

  const enabled = debouncedInput !== '' && autocompleteValue === '';

  const {
    data: sharedDrivesPage,
    isLoading: sharedDrivesLoading,
    isFetching: sharedDrivesFetching,
  } = useQuery({
    queryKey: [UserKeys.SharedDrivesForDomainPage, { debouncedInput }],
    queryFn: () => {
      const object: Page<SharedDriveModel[]> = {
        cursor: null,
        results: [],
        nameSpace: '',
        firstPull: true,
        searchTime: 0,
        pageSize: 5,
      };
      return getPageOfSharedDrivesForDomain(object, debouncedInput, '');
    },
    enabled: enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const options: SharedDriveModel[] = sharedDrivesPage?.results || [];

  function createNoOptionsText() {
    if (debouncedInput === '') {
      return 'Start typing to search...';
    }
    return 'No shared drives found.';
  }

  const key = useMemo(
    () => newKey || new Date().getTime().toString(),
    [newKey]
  );

  return (
    <Autocomplete
      key={key}
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      size={size}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 350,
          },
        },
      }}
      autoComplete
      autoHighlight
      includeInputInList
      filterSelectedOptions
      noOptionsText={createNoOptionsText()}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        return option.name;
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string') {
          return option === value;
        } else {
          return option.id === value?.id;
        }
      }}
      onChange={(_event, newValue) => {
        if (typeof newValue === 'string') {
          setAutoCompleteValue(newValue);
          onDriveSelect?.(null);
        } else {
          setAutoCompleteValue(newValue?.name || '');
          onDriveSelect?.(newValue || null);
        }
      }}
      renderOption={(props, option: SharedDriveModel) => {
        return (
          <ListItem {...props} key={option.id} dense>
            <ListItemAvatar>
              <Avatar
                src={option.backgroundImageLink}
                sx={{ width: 40, height: 40 }}
              />
            </ListItemAvatar>
            <ListItemText
              id={option.id}
              primary={option.name}
              secondary={`Drive ID: ${option.id || ''}`}
              primaryTypographyProps={{ whiteSpace: 'normal' }}
            />
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={'Search for a shared drive'}
          required={required}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {sharedDrivesLoading || sharedDrivesFetching ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={errors}
          helperText={helperText}
        />
      )}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
    />
  );
}
